import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  ArrowRight, Sparkle, LockSimple, CloudCheck, Cpu, ShieldCheck, Trash, Brain,
  Lightning, Code, Image, FileText, Microphone, ArrowsLeftRight, Circuitry, Detective
} from '@phosphor-icons/react';

const fadeUp = {
  initial: { opacity: 0, y: 24 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.6, ease: [0.16, 1, 0.3, 1] }
};

const stagger = { animate: { transition: { staggerChildren: 0.08 } } };

const glass = 'backdrop-blur-xl bg-white/[0.06] border border-white/10';

export default function LandingPage() {
  return (
    <div className="min-h-screen bg-[#07090d] text-white relative overflow-hidden" data-testid="landing-page" style={{ fontFamily: 'Satoshi, sans-serif' }}>
      {/* Aurora background */}
      <div className="pointer-events-none fixed inset-0 z-0">
        <div className="aurora-blob-1 absolute -top-40 -left-40 w-[600px] h-[600px] rounded-full bg-lime-500/20 blur-[140px]" />
        <div className="aurora-blob-2 absolute top-1/3 -right-52 w-[560px] h-[560px] rounded-full bg-teal-400/10 blur-[160px]" />
        <div className="aurora-blob-3 absolute bottom-0 left-1/3 w-[500px] h-[500px] rounded-full bg-lime-300/10 blur-[150px]" />
      </div>

      <div className="relative z-10">
        {/* Nav */}
        <nav className={`fixed top-4 left-1/2 -translate-x-1/2 z-50 w-[calc(100%-2rem)] max-w-5xl rounded-2xl ${glass}`}>
          <div className="px-5 h-14 flex items-center justify-between">
            <Link to="/" className="flex items-center gap-2" data-testid="nav-logo">
              <div className="w-7 h-7 rounded-lg bg-lime-400 flex items-center justify-center">
                <Sparkle weight="fill" className="w-4 h-4 text-black" />
              </div>
              <span className="text-lg font-semibold tracking-tight" style={{ fontFamily: 'Clash Display, sans-serif' }}>Lindey</span>
            </Link>
            <div className="flex items-center gap-3">
              <Link to="/auth" className="text-sm text-white/60 hover:text-white transition-colors px-3 py-1.5" data-testid="nav-signin">
                Sign in
              </Link>
              <Link to="/auth" className="text-sm font-semibold bg-lime-400 hover:bg-lime-300 text-black px-4 py-2 rounded-xl transition-colors" data-testid="nav-get-started">
                Get started free
              </Link>
            </div>
          </div>
        </nav>

        {/* Hero */}
        <section className="pt-40 pb-16 px-6" data-testid="hero-section">
          <motion.div className="max-w-4xl mx-auto text-center" variants={stagger} initial="initial" animate="animate">
            <motion.div variants={fadeUp} className={`inline-flex items-center gap-2 px-4 py-1.5 rounded-full ${glass} text-sm text-lime-300 mb-7`} data-testid="linkletter-badge">
              <Lightning weight="fill" className="w-4 h-4 text-orange-400" />
              NEW — Lindey Heavy: up to 60 models, orchestrated on your device
            </motion.div>

            <motion.h1
              variants={fadeUp}
              className="text-5xl sm:text-6xl lg:text-7xl font-bold tracking-tighter leading-[0.95] mb-6"
              style={{ fontFamily: 'Clash Display, sans-serif' }}
            >
              Private by design.<br />
              <span className="shimmer-text">Powerful by default.</span>
            </motion.h1>

            <motion.p variants={fadeUp} className="text-lg text-white/50 max-w-2xl mx-auto mb-10 leading-relaxed">
              Lindey runs AI directly on your device — and now, Lindey Heavy ignites up to 60 of our most
              powerful models at once for massive on-device processing. Your questions never have to leave your browser.
            </motion.p>

            <motion.div variants={fadeUp} className="flex items-center justify-center gap-3 flex-wrap">
              <Link to="/auth" className="inline-flex items-center gap-2 bg-lime-400 hover:bg-lime-300 text-black font-semibold px-7 py-3.5 rounded-xl text-sm transition-all hover:-translate-y-0.5" data-testid="hero-cta">
                Start using Lindey
                <ArrowRight weight="bold" className="w-4 h-4" />
              </Link>
              <a href="#privacy" className={`inline-flex items-center gap-2 ${glass} hover:bg-white/10 text-white/80 font-medium px-7 py-3.5 rounded-xl text-sm transition-all hover:-translate-y-0.5`} data-testid="hero-learn-more">
                How privacy works
              </a>
            </motion.div>
          </motion.div>
        </section>

        {/* Marquee ticker */}
        <div className="border-y border-white/10 py-3 overflow-hidden" data-testid="marquee-ticker">
          <div className="marquee-track">
            {[0, 1].map(dup => (
              <div key={dup} className="flex items-center gap-10 pr-10 shrink-0">
                {['LINDEY HEAVY — 60-MODEL ORCHESTRATION', 'ON-DEVICE BY DEFAULT', 'PRIVATE CLOUD COMPUTE', 'ZERO LOGS · ZERO TRACKING', 'THE LINKLETTER PROGRAM', 'DELETE MY DATA — ONE TAP', 'WEBGPU INFERENCE', 'SMART CPU SCHEDULER'].map((t, i) => (
                  <span key={i} className="flex items-center gap-3 text-[11px] font-mono uppercase tracking-[0.25em] text-white/35 whitespace-nowrap">
                    <span className={`w-1.5 h-1.5 rounded-full ${i % 2 ? 'bg-orange-400/70' : 'bg-lime-400/70'}`} />{t}
                  </span>
                ))}
              </div>
            ))}
          </div>
        </div>

        {/* Lindey Heavy showcase */}
        <section id="heavy" className="py-20 px-6" data-testid="heavy-section">
          <motion.div className="max-w-5xl mx-auto" initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.7 }}>
            <div className="rounded-3xl backdrop-blur-xl bg-white/[0.04] border border-lime-400/25 heavy-border-glow p-8 sm:p-12 overflow-hidden relative">
              <div className="grid lg:grid-cols-2 gap-10 items-center">
                <div>
                  <span className="inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-[0.3em] px-3 py-1 rounded-full bg-gradient-to-r from-lime-400 to-orange-400 text-black mb-5">
                    <Lightning weight="fill" className="w-3 h-3" /> New
                  </span>
                  <h2 className="text-4xl sm:text-5xl font-bold tracking-tighter mb-4" style={{ fontFamily: 'Clash Display, sans-serif' }}>
                    Meet <span className="heavy-gradient-text">Lindey Heavy</span>
                  </h2>
                  <p className="text-white/55 leading-relaxed mb-6">
                    One tap, one cinematic power surge — and the CPU scheduler goes into full effect,
                    orchestrating up to <span className="text-lime-300 font-semibold">60 of our most powerful models</span> together.
                    Planners, solvers and critics debate in parallel until only the best answer survives.
                    More powerful than ever. Working through complex problems entirely on your device.
                  </p>
                  <div className="flex gap-6 mb-8">
                    {[['60', 'model shards'], ['5', 'reasoning clusters'], ['0', 'servers involved']].map(([n, l], i) => (
                      <div key={i}>
                        <p className="text-3xl font-bold heavy-gradient-text" style={{ fontFamily: 'Clash Display, sans-serif' }}>{n}</p>
                        <p className="text-[11px] uppercase tracking-widest text-white/35 mt-1">{l}</p>
                      </div>
                    ))}
                  </div>
                  <Link to="/auth" className="inline-flex items-center gap-2 bg-gradient-to-r from-lime-400 to-orange-400 hover:brightness-110 text-black font-bold px-7 py-3.5 rounded-xl text-sm transition-all hover:-translate-y-0.5" data-testid="heavy-cta">
                    Ignite Heavy mode
                    <Lightning weight="fill" className="w-4 h-4" />
                  </Link>
                </div>
                <div className="grid grid-cols-10 gap-1.5" data-testid="heavy-landing-grid">
                  {Array.from({ length: 60 }).map((_, i) => (
                    <span key={i} className="landing-shard h-6 sm:h-8" style={{ animationDelay: `${(i % 10) * 0.18 + Math.floor(i / 10) * 0.32}s` }} />
                  ))}
                </div>
              </div>
            </div>
          </motion.div>
        </section>

        {/* Orchestrator flow */}
        <section id="privacy" className="py-16 px-6" data-testid="orchestrator-section">
          <div className="max-w-5xl mx-auto">
            <motion.div className="text-center mb-12" initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5 }}>
              <p className="text-sm uppercase tracking-[0.25em] text-lime-400 font-medium mb-3">Hybrid Intelligence</p>
              <h2 className="text-3xl sm:text-4xl font-semibold tracking-tight" style={{ fontFamily: 'Clash Display, sans-serif' }}>
                The System Orchestrator decides.<br className="hidden sm:block" /> Your data stays yours.
              </h2>
            </motion.div>

            <motion.div className="grid grid-cols-1 md:grid-cols-3 gap-5" variants={stagger} initial="initial" whileInView="animate" viewport={{ once: true }}>
              {[
                { icon: Circuitry, step: '01', title: 'Orchestrator analyzes', desc: 'Every request is inspected locally first. A privacy layer in your browser decides where it can safely run — before anything leaves your device.' },
                { icon: LockSimple, step: '02', title: 'On-device by default', desc: 'Everyday questions run on Lindey models inside your browser via WebGPU. No servers, no logs, no network. It literally cannot be subpoenaed.' },
                { icon: CloudCheck, step: '03', title: 'Private Cloud Compute', desc: 'Heavy tasks — agents, images, deep reasoning — route to our stateless cloud. Data is processed in memory and immediately vaporized.' },
              ].map((f, i) => (
                <motion.div key={i} variants={fadeUp} className={`rounded-2xl ${glass} p-6 hover:bg-white/[0.09] transition-colors`} data-testid={`orchestrator-card-${i}`}>
                  <div className="flex items-center justify-between mb-5">
                    <div className="w-11 h-11 rounded-xl bg-lime-400/10 border border-lime-400/20 flex items-center justify-center">
                      <f.icon weight="duotone" className="w-5 h-5 text-lime-400" />
                    </div>
                    <span className="text-xs font-mono text-white/30">{f.step}</span>
                  </div>
                  <h3 className="text-base font-semibold mb-2" style={{ fontFamily: 'Clash Display, sans-serif' }}>{f.title}</h3>
                  <p className="text-sm text-white/50 leading-relaxed">{f.desc}</p>
                </motion.div>
              ))}
            </motion.div>
          </div>
        </section>

        {/* Comparison */}
        <section className="py-16 px-6" data-testid="comparison-section">
          <motion.div className="max-w-4xl mx-auto" initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }}>
            <div className={`rounded-2xl ${glass} overflow-hidden`}>
              <div className="grid grid-cols-3 text-sm">
                <div className="p-4 sm:p-5 border-b border-white/10 text-white/40 font-medium" />
                <div className="p-4 sm:p-5 border-b border-l border-white/10">
                  <div className="flex items-center gap-2 font-semibold text-lime-300"><LockSimple weight="fill" className="w-4 h-4 shrink-0" /> On-Device</div>
                </div>
                <div className="p-4 sm:p-5 border-b border-l border-white/10">
                  <div className="flex items-center gap-2 font-semibold text-white/80"><CloudCheck weight="fill" className="w-4 h-4 shrink-0" /> Private Cloud</div>
                </div>
                {[
                  ['Best for', 'Personal questions, everyday chat, sensitive topics', 'Agents, image generation, deep reasoning'],
                  ['Data handling', 'Never leaves your browser', 'Processed in memory, then vaporized'],
                  ['Hardware', 'Your device, via WebGPU', 'Lindey compute nodes'],
                  ['Who can see it', 'Only you', 'No one — stateless, no logs'],
                ].map(([label, a, b], i) => (
                  <React.Fragment key={i}>
                    <div className={`p-4 sm:p-5 text-white/40 ${i < 3 ? 'border-b border-white/10' : ''}`}>{label}</div>
                    <div className={`p-4 sm:p-5 border-l border-white/10 text-white/80 ${i < 3 ? 'border-b' : ''}`}>{a}</div>
                    <div className={`p-4 sm:p-5 border-l border-white/10 text-white/60 ${i < 3 ? 'border-b' : ''}`}>{b}</div>
                  </React.Fragment>
                ))}
              </div>
            </div>
          </motion.div>
        </section>

        {/* Local models */}
        <section className="py-16 px-6" data-testid="local-models-section">
          <div className="max-w-5xl mx-auto">
            <motion.div className="text-center mb-12" initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5 }}>
              <p className="text-sm uppercase tracking-[0.25em] text-lime-400 font-medium mb-3">Lindey On-Device Models</p>
              <h2 className="text-3xl sm:text-4xl font-semibold tracking-tight" style={{ fontFamily: 'Clash Display, sans-serif' }}>
                Three models. Zero servers.
              </h2>
            </motion.div>
            <motion.div className="grid grid-cols-1 md:grid-cols-3 gap-5" variants={stagger} initial="initial" whileInView="animate" viewport={{ once: true }}>
              {[
                { icon: Lightning, name: 'Lindey Fast', size: '~400 MB', desc: 'Instant answers on any laptop or phone. The lightest download, the quickest replies.' },
                { icon: Brain, name: 'Lindey Thinking', size: '~800 MB', desc: 'Balanced speed and intelligence for richer conversations, fully offline-capable.' },
                { icon: Cpu, name: 'Lindey Pro', size: '~1.8 GB', desc: 'Our smartest on-device model. The default — with a smart scheduler that adapts to your hardware.', featured: true },
              ].map((m, i) => (
                <motion.div key={i} variants={fadeUp} className={`rounded-2xl p-6 transition-colors ${m.featured ? 'backdrop-blur-xl bg-lime-400/10 border border-lime-400/30' : `${glass} hover:bg-white/[0.09]`}`} data-testid={`local-model-card-${i}`}>
                  <div className="flex items-center justify-between mb-5">
                    <div className={`w-11 h-11 rounded-xl flex items-center justify-center ${m.featured ? 'bg-lime-400 text-black' : 'bg-white/5 border border-white/10 text-lime-400'}`}>
                      <m.icon weight="fill" className="w-5 h-5" />
                    </div>
                    <span className="text-xs font-mono text-white/40">{m.size}</span>
                  </div>
                  <h3 className="text-base font-semibold mb-2" style={{ fontFamily: 'Clash Display, sans-serif' }}>{m.name}</h3>
                  <p className="text-sm text-white/50 leading-relaxed">{m.desc}</p>
                  {m.featured && <span className="inline-block mt-4 text-[10px] font-semibold uppercase tracking-widest text-lime-300">Default</span>}
                </motion.div>
              ))}
            </motion.div>
          </div>
        </section>

        {/* Privacy pledge */}
        <section className="py-16 px-6" data-testid="privacy-pledge-section">
          <motion.div className="max-w-4xl mx-auto" initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }}>
            <div className={`rounded-3xl ${glass} p-8 sm:p-12`}>
              <div className="flex flex-col sm:flex-row gap-8 items-start">
                <div className="w-14 h-14 rounded-2xl bg-lime-400 flex items-center justify-center shrink-0">
                  <Detective weight="fill" className="w-7 h-7 text-black" />
                </div>
                <div>
                  <h2 className="text-2xl sm:text-3xl font-semibold tracking-tight mb-3" style={{ fontFamily: 'Clash Display, sans-serif' }}>
                    The Linkletter Pledge
                  </h2>
                  <p className="text-white/50 leading-relaxed mb-6">
                    We built Lindey so the safest place for your thoughts is the one you already own — your device.
                    What we don't store, we can't hand over. And whatever we do store, you can erase in one tap.
                  </p>
                  <div className="grid sm:grid-cols-3 gap-4">
                    {[
                      { icon: LockSimple, t: 'On-device first', d: 'Default processing never touches a server' },
                      { icon: Trash, t: 'Delete My Data', d: 'One button wipes every chat, file and task' },
                      { icon: ShieldCheck, t: 'Stateless cloud', d: 'Cloud requests are never logged or retained' },
                    ].map((p, i) => (
                      <div key={i} className="rounded-xl bg-white/[0.04] border border-white/10 p-4">
                        <p.icon weight="fill" className="w-4 h-4 text-lime-400 mb-2" />
                        <p className="text-sm font-semibold mb-1">{p.t}</p>
                        <p className="text-xs text-white/40 leading-relaxed">{p.d}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </section>

        {/* Capabilities */}
        <section id="features" className="py-16 px-6" data-testid="features-section">
          <div className="max-w-5xl mx-auto">
            <motion.div className="text-center mb-12" initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5 }}>
              <p className="text-sm uppercase tracking-[0.25em] text-lime-400 font-medium mb-3">Capabilities</p>
              <h2 className="text-3xl sm:text-4xl font-semibold tracking-tight" style={{ fontFamily: 'Clash Display, sans-serif' }}>
                Everything Lindey could do. Now safer.
              </h2>
            </motion.div>
            <motion.div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" variants={stagger} initial="initial" whileInView="animate" viewport={{ once: true }}>
              {[
                { icon: Brain, title: 'Agent Mode', desc: 'Hand off complex multi-step tasks. Lindey plans, executes, and shows every step.' },
                { icon: Code, title: 'Code Generation', desc: 'Write, debug, explain, and refactor code in any language with syntax highlighting.' },
                { icon: Image, title: 'Image Generation', desc: 'Describe what you imagine. Lindey creates it instantly, right in the chat.' },
                { icon: FileText, title: 'File Analysis', desc: 'Upload PDFs, docs, images, CSVs — Lindey reads, summarizes, and answers.' },
                { icon: Microphone, title: 'Voice I/O', desc: 'Speak to Lindey and hear responses back, built into the browser.' },
                { icon: ArrowsLeftRight, title: 'Import Anywhere', desc: 'One-click import of your full ChatGPT or Claude history. Pick up where you left off.' },
              ].map((f, i) => (
                <motion.div key={i} variants={fadeUp} className={`rounded-2xl ${glass} p-5 hover:bg-white/[0.09] transition-colors`} data-testid={`feature-card-${i}`}>
                  <div className="w-9 h-9 rounded-lg bg-lime-400/10 border border-lime-400/20 flex items-center justify-center mb-3">
                    <f.icon weight="duotone" className="w-4.5 h-4.5 text-lime-400" style={{ width: 18, height: 18 }} />
                  </div>
                  <h3 className="text-sm font-semibold mb-1" style={{ fontFamily: 'Clash Display, sans-serif' }}>{f.title}</h3>
                  <p className="text-xs text-white/45 leading-relaxed">{f.desc}</p>
                </motion.div>
              ))}
            </motion.div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-24 px-6" data-testid="cta-section">
          <motion.div className="max-w-2xl mx-auto text-center" initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5 }}>
            <h2 className="text-3xl sm:text-4xl font-semibold tracking-tight mb-4" style={{ fontFamily: 'Clash Display, sans-serif' }}>
              Your AI. Your device. Your rules.
            </h2>
            <p className="text-white/45 mb-8">No credit card. No tracking. Just start chatting.</p>
            <Link to="/auth" className="inline-flex items-center gap-2 bg-lime-400 hover:bg-lime-300 text-black font-semibold px-8 py-4 rounded-xl text-sm transition-all hover:-translate-y-0.5" data-testid="cta-button">
              Get started free
              <ArrowRight weight="bold" className="w-4 h-4" />
            </Link>
          </motion.div>
        </section>

        {/* Footer */}
        <footer className="border-t border-white/10 py-8 px-6">
          <div className="max-w-5xl mx-auto flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="w-5 h-5 rounded bg-lime-400 flex items-center justify-center">
                <Sparkle weight="fill" className="w-3 h-3 text-black" />
              </div>
              <span className="text-sm font-medium">Lindey</span>
            </div>
            <p className="text-xs text-white/30">The Linkletter Program · Private by design.</p>
          </div>
        </footer>
      </div>
    </div>
  );
}
